export interface Article {
  id: number;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  coverImageSeq: string;
  date: string;
  readTime: string;
  author: string;
  content: ArticleSection[];
  tags: string[];
}

export interface ArticleSection {
  type: 'paragraph' | 'heading' | 'image' | 'quote';
  content: string;
  imageSeq?: string;
}

export const articleCategories = [
  { id: 'brand', name: '品牌故事' },
  { id: 'lifestyle', name: '生活美学' },
  { id: 'culture', name: '诗经文化' },
  { id: 'product', name: '产品灵感' },
];

export const articles: Article[] = [
  {
    id: 1,
    slug: 'youyou-brand-story',
    title: '呦呦鹿鸣，食野之苹——品牌诞生记',
    excerpt: '从《诗经·小雅》到现代生活，呦呦品牌的诞生源于对东方美学的深深眷恋，我们希望能将千年诗韵融入当下的每一刻。',
    category: 'brand',
    coverImageSeq: 'article-01',
    date: '2026-08-01',
    readTime: '8分钟',
    author: '呦呦编辑部',
    tags: ['品牌故事', '诗经', '东方美学'],
    content: [
      { type: 'heading', content: '一、缘起：诗经里的名字' },
      { type: 'paragraph', content: '"呦呦鹿鸣，食野之苹。我有嘉宾，鼓瑟吹笙。"——《诗经·小雅·鹿鸣》这首三千年前的宴饮诗，描绘了鹿群在原野上悠然鸣叫、咀嚼芬芳草木的和谐画面。当我们的创始团队在一次文化之旅中重新读到这首诗时，一种跨越千年的共鸣在心中激荡。' },
      { type: 'paragraph', content: '"呦呦"二字，取自鹿鸣之声，是自然最纯粹的表达。它没有矫饰，没有造作，只是生命本真的回响。这正是我们想传递的品牌精神——回归自然，拥抱纯粹。' },
      { type: 'image', content: '', imageSeq: 'article-01-img-01' },
      { type: 'heading', content: '二、探索：东方美学的现代转译' },
      { type: 'paragraph', content: '东方美学从来不是博物馆里的陈列品，而是流淌在生活中的诗意。从宋瓷的温润釉色到明式家具的简约线条，从江南园林的移步换景到文人画的留白意境，这些美学传统给予我们无尽的灵感。' },
      { type: 'paragraph', content: '我们走访了数十位传统工艺匠人——制陶师傅、茶艺大师、香道传承人。从他们手中，我们学会了慢下来，学会了敬畏自然赋予每一种材料的天性。这些珍贵的经验，最终凝结为呦呦产品的灵魂。' },
      { type: 'quote', content: '呦呦之美，不在于张扬夺目，而在于那种经得起时间沉淀的温润与从容。' },
      { type: 'heading', content: '三、生长：从一款产品到一个世界' },
      { type: 'paragraph', content: '第一款产品"呦呦·清露精华液"诞生于一个春天。研发团队花费了整整两年时间，从上百种植物原料中筛选出最能与肌肤共鸣的组合。芍药的柔润、白芷的清透，在精心配比下达到了微妙的平衡。' },
      { type: 'paragraph', content: '产品上市后，我们收到了许多来自用户的暖心反馈。有人说"用呦呦的产品，像是在做一场穿越千年的梦"；有人说"终于找到了一个能读懂东方美学的品牌"。这些声音，是我们前行的最大动力。' },
      { type: 'image', content: '', imageSeq: 'article-01-img-02' },
      { type: 'heading', content: '四、展望：让每一天都充满呦呦之美' },
      { type: 'paragraph', content: '今天的呦呦，已经形成了护肤、香氛、茶品、家居四大系列。每一款产品都承载着一句诗经、一种意境、一份匠心。但我们深知，这只是一个开始。' },
      { type: 'paragraph', content: '我们期待与更多热爱东方文化、追求品质生活的朋友相遇。在这个快节奏的时代，让我们一起慢下来，在呦呦的世界里，找回那份源自千年前的自然与纯粹。' },
    ],
  },
  {
    id: 2,
    slug: 'oriental-skincare-wisdom',
    title: '《诗经》里的护肤智慧：千年草本的美颜秘方',
    excerpt: '翻开《诗经》，你会发现古人对草木之美的理解远超想象。这些千年草本蕴含着怎样的护肤智慧？让我们一起探寻。',
    category: 'product',
    coverImageSeq: 'article-02',
    date: '2026-07-28',
    readTime: '6分钟',
    author: '呦呦研究院',
    tags: ['护肤', '诗经', '草本', '东方智慧'],
    content: [
      { type: 'heading', content: '古老智慧中的美颜哲学' },
      { type: 'paragraph', content: '在《诗经》的305篇诗歌中，提及的植物多达一百三十余种。古人不仅用它们吟咏情志，更善于发掘其养生驻颜的功效。芍药、白芷、薏苡、菟丝子……这些美丽的名字背后，藏着千百年来女性守护容颜的秘密。' },
      { type: 'image', content: '', imageSeq: 'article-02-img-01' },
      { type: 'heading', content: '芍药——"维士与女，伊其将谑，赠之以勺药"' },
      { type: 'paragraph', content: '《诗经·郑风·溱洧》中的芍药，是古代男女相赠的信物，也是重要的养颜本草。芍药根含有丰富的芍药苷，具有卓越的抗氧化和舒缓功效，能够帮助肌肤抵御环境侵害，恢复天然光泽。' },
      { type: 'heading', content: '白芷——"彼采萧兮，一日不见，如三秋兮"' },
      { type: 'paragraph', content: '白芷在《诗经》中常与香草并列，其根茎自古便是美白祛斑的良方。现代研究发现，白芷提取物能有效抑制酪氨酸酶活性，从源头减少黑色素生成，让肌肤由内而外透出莹润光泽。' },
      { type: 'quote', content: '以《诗经》草木为引，将千年草本智慧注入现代护肤科技，这是呦呦产品研发的核心哲学。' },
      { type: 'heading', content: '古今融合：传统与现代的完美对话' },
      { type: 'paragraph', content: '呦呦的研发团队深耕古籍，同时与现代皮肤科学研究相结合。每一款产品的配方，都是对一种《诗经》草木的深度解读与创新应用。我们相信，最好的护肤品不是化学合成的，而是大自然慷慨馈赠的。' },
      { type: 'paragraph', content: '从采摘到萃取，从配比到测试，每一个环节都严格把控。我们与国内顶尖的植物研究所合作，确保每一种原料都来自最佳产地，保留最完整的活性成分。' },
      { type: 'image', content: '', imageSeq: 'article-02-img-02' },
      { type: 'paragraph', content: '未来，呦呦将继续在古籍与现代科技之间探索，让更多源自《诗经》的草本力量，以科学的方式呵护你的肌肤。因为最美的妆容，是自然赠予的健康光彩。' },
    ],
  },
  {
    id: 3,
    slug: 'four-seasons-tea-living',
    title: '四时茶事：在茶香中感受东方生活节奏',
    excerpt: '春生夏长，秋收冬藏。四季轮回间，一壶清茶如何陪伴我们的日常？这份四时茶事指南，带你体验东方茶生活的韵律之美。',
    category: 'lifestyle',
    coverImageSeq: 'article-03',
    date: '2026-07-20',
    readTime: '7分钟',
    author: '呦呦茶事',
    tags: ['茶道', '生活美学', '四时', '养生'],
    content: [
      { type: 'heading', content: '茶，中国人骨子里的生活方式' },
      { type: 'paragraph', content: '"开门七件事，柴米油盐酱醋茶。"茶在中国人的生活中，从来不只是饮品。它是一种节奏，一种仪式，一种与天地自然对话的方式。' },
      { type: 'paragraph', content: '在呦呦的世界里，我们倡导"四时茶事"的生活理念——顺应四季变化，选择适宜的茶品，让每一杯茶都恰逢其时。' },
      { type: 'image', content: '', imageSeq: 'article-03-img-01' },
      { type: 'heading', content: '春·生发：绿茶清心' },
      { type: 'paragraph', content: '春天万物复苏，阳气生发。此时最适合品饮明前绿茶，如呦呦·春涧绿茶。嫩芽在杯中舒展，仿佛整个春天都在口中绽放。绿茶的清新鲜爽，能帮助我们驱散一冬的沉闷，迎接新一年的生机。' },
      { type: 'heading', content: '夏·繁茂：白茶消暑' },
      { type: 'paragraph', content: '盛夏炎炎，一杯清凉的白茶最为适宜。呦呦·南山白茶采自高山有机茶园，汤色浅杏，清甜回甘。白茶性凉，在酷暑中带来一丝清凉，又不伤脾胃，是夏季养生的佳选。' },
      { type: 'quote', content: '茶之一道，不在名贵，而在于适时、适地、适人、适心。' },
      { type: 'heading', content: '秋·收敛：乌龙养润' },
      { type: 'paragraph', content: '秋高气爽，气候转燥。此时半发酵的乌龙茶最能滋养身心。呦呦·幽谷乌龙，花香馥郁层次丰富，温润不燥，非常适合秋天饮用，既能滋润喉咙，又能在微凉秋日带来温暖。' },
      { type: 'heading', content: '冬·储藏：红茶普洱暖身' },
      { type: 'paragraph', content: '冬日严寒，适合品饮全发酵的红茶和后发酵的普洱。呦呦·山月红茶温润醇厚，暖入心脾；呦呦·松风普洱岁月陈香，在炭火旁慢慢品味，感受时光在舌尖流淌。' },
      { type: 'image', content: '', imageSeq: 'article-03-img-02' },
      { type: 'paragraph', content: '四时流转，一期一会。在呦呦的茶世界中，我们期待你找到属于自己的那份宁静与美好。' },
    ],
  },
  {
    id: 4,
    slug: 'book-of-songs-and-life',
    title: '《诗经》里的生活美学：三千年前的极简主义',
    excerpt: '翻阅《诗经》，你会发现三千年前的人们已经掌握了极简生活的精髓。这些古老的诗句，如何指引我们今天的生活？',
    category: 'culture',
    coverImageSeq: 'article-04',
    date: '2026-07-15',
    readTime: '9分钟',
    author: '呦呦文化',
    tags: ['诗经', '极简主义', '生活哲学', '东方文化'],
    content: [
      { type: 'heading', content: '古老诗歌中的现代启示' },
      { type: 'paragraph', content: '当"极简主义"成为当代生活的流行词汇时，很少有人意识到，三千年前的《诗经》中早已蕴含着极简生活的全部智慧。那些简朴而优美的诗句，描绘的正是人与自然、人与内心和谐共处的最理想状态。' },
      { type: 'image', content: '', imageSeq: 'article-04-img-01' },
      { type: 'heading', content: '"采采卷耳，不盈顷筐"——少即是多' },
      { type: 'paragraph', content: '《周南·卷耳》中的这句诗，描述的是一位女子采摘卷耳却久久未能装满竹筐。她并不焦急，而是沉浸在这一简单的劳作中。这就是极简主义的精髓——专注于当下，不贪多，不浮躁。' },
      { type: 'heading', content: '"衡门之下，可以栖迟"——简朴即丰盛' },
      { type: 'paragraph', content: '《陈风·衡门》告诉我们，在简陋的横门之下也可以安然栖居。物质的简朴并不妨碍精神的丰盛。在如今物质极大丰富的时代，这句诗提醒我们重新审视什么是真正必要的。' },
      { type: 'quote', content: '呦呦所倡导的生活美学，本质上是一种"诗经式"的极简——珍惜自然的馈赠，追求内心的安宁，在简约中发现丰盈。' },
      { type: 'heading', content: '将诗经极简融入日常' },
      { type: 'paragraph', content: '我们不需要回到三千年前的生活方式，但可以从《诗经》中汲取智慧：选择少而精的物品，珍惜每一件器物的温度；放慢生活的节奏，给心灵留出呼吸的空间；回归自然的怀抱，让草木的气息治愈疲惫的身心。' },
      { type: 'image', content: '', imageSeq: 'article-04-img-02' },
      { type: 'paragraph', content: '这正是呦呦家居系列的灵感来源——用简约而有温度的器物，装点每一个日常瞬间，让生活成为诗。' },
    ],
  },
  {
    id: 5,
    slug: 'fragrance-and-poetry',
    title: '闻香识诗：当香氛遇见《诗经》意境',
    excerpt: '香氛是无声的诗，诗歌是有味的香。呦呦香氛系列如何将《诗经》的美妙意境转化为嗅觉体验？',
    category: 'product',
    coverImageSeq: 'article-05',
    date: '2026-07-08',
    readTime: '5分钟',
    author: '呦呦香道',
    tags: ['香氛', '诗经', '调香', '意境'],
    content: [
      { type: 'heading', content: '气味是最古老的记忆' },
      { type: 'paragraph', content: '普鲁斯特效应告诉我们，气味能唤起最强烈的情感记忆。而在《诗经》中，草木的芬芳无处不在——"蒹葭苍苍"的水边芦苇，"桃之夭夭"的春日桃花，"呦呦鹿鸣"的原野青草……每一种香气都是一扇通往千年前的任意门。' },
      { type: 'image', content: '', imageSeq: 'article-05-img-01' },
      { type: 'heading', content: '鹿鸣：原野草木调' },
      { type: 'paragraph', content: '呦呦·鹿鸣香薰的灵感源自《小雅·鹿鸣》。调香师以青草、苔藓为基底，融入野菊、薄荷的清冽，再以檀木收尾。整体香调如漫步清晨的原野，清新空灵，让人在呼吸间感受自然的辽阔。' },
      { type: 'heading', content: '桃夭：花果明媚调' },
      { type: 'paragraph', content: '呦呦·桃夭香氛取自《周南·桃夭》"桃之夭夭，灼灼其华"。以桃花为主调，辅以柑橘的前调清新和蜂蜜的后调温暖，整体香气明媚而不甜腻，如春日里一位身着粉色罗裙的女子，笑靥如花。' },
      { type: 'quote', content: '每一款呦呦香氛，都是一首可以闻的诗。' },
      { type: 'image', content: '', imageSeq: 'article-05-img-02' },
      { type: 'paragraph', content: '我们相信，香氛的力量不在于浓烈，而在于它能唤起的那份内心深处的共鸣。当你在家中点燃一支呦呦蜡烛，也许你会想起某句遥远却熟悉的诗句，那是千年前与你心意相通的声音。' },
    ],
  },
  {
    id: 6,
    slug: 'minimalist-home-aesthetics',
    title: '简而不凡：打造东方极简家居空间的五个法则',
    excerpt: '极简不是空无一物，而是每一件都恰如其分。跟随呦呦，学习用五个法则打造有温度、有灵魂的东方极简家居。',
    category: 'lifestyle',
    coverImageSeq: 'article-06',
    date: '2026-06-30',
    readTime: '7分钟',
    author: '呦呦家居',
    tags: ['家居', '极简', '东方美学', '空间设计'],
    content: [
      { type: 'heading', content: '法则一：留白之美' },
      { type: 'paragraph', content: '中国画的最高境界是"计白当黑"。在家居空间中，留白不是空洞，而是给目光和心灵留出呼吸的空间。一个角落、一面墙，不必填满。少即是多，空即是满。' },
      { type: 'image', content: '', imageSeq: 'article-06-img-01' },
      { type: 'heading', content: '法则二：材质的温度' },
      { type: 'paragraph', content: '东方极简偏爱自然材质——木的温润、竹的清雅、陶的质朴、麻的自然。不同材质之间的对话，创造出丰富而有层次的空间感受。呦呦·清风竹编和呦呦·幽兰茶具，正是这一理念的具象表达。' },
      { type: 'heading', content: '法则三：光与影的游戏' },
      { type: 'paragraph', content: '光是最好的设计师。晨光、午光、夕光，不同时刻的光线赋予空间不同的情绪。呦呦·月色香炉在光影变化中呈现出不同的美感，让日常生活充满诗意。' },
      { type: 'heading', content: '法则四：一物一用，一器一心' },
      { type: 'paragraph', content: '选择少而精的物品，每一件都经过精心挑选，都有它存在的意义。与其拥有十件平庸的物件，不如珍藏一件能触动心灵的好物。' },
      { type: 'quote', content: '最好的家居，不是展示给别人看的，而是让自己每一天都感到舒适与安宁。' },
      { type: 'heading', content: '法则五：融入自然元素' },
      { type: 'paragraph', content: '一枝枯木、一块奇石、一盆绿植，自然元素为家居空间注入生命力。东方美学讲究"师法自然"，让家成为人与自然对话的场所。' },
      { type: 'image', content: '', imageSeq: 'article-06-img-02' },
      { type: 'paragraph', content: '这五个法则不是教条，而是一种态度。在呦呦的世界里，我们希望陪伴你，一点点打造那个只属于你的诗意空间。' },
    ],
  },
];